
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { CompressionResult } from '@/types/imageFile';
import { CompressionConfig } from '../CompressionSettings';
import ImageComparisonView from './ImageComparisonView';
import ImageSingleView from './ImageSingleView';
import FileInfoSection from './FileInfoSection';
import ActionButtons from './ActionButtons';
import StatusBadge from './StatusBadge';

interface ImagePreviewProps {
  file: File;
  compression?: CompressionResult;
  isCompressing: boolean;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  retryCount: number;
  maxRetryAttempts: number;
  onRemove: () => void;
  onRetry: () => void;
  onCompress: () => void;
  compressionConfig: CompressionConfig;
  isPremium?: boolean;
}

const ImagePreview = ({ 
  file, 
  compression,
  isCompressing,
  status,
  error,
  retryCount,
  maxRetryAttempts,
  onRemove, 
  onRetry,
  onCompress,
  compressionConfig,
  isPremium = false
}: ImagePreviewProps) => {
  const [originalUrl, setOriginalUrl] = useState<string>('');
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(true);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setOriginalUrl(url);
    
    const img = new Image();
    img.onload = () => setIsLoadingOriginal(false);
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg group ${
      isPremium ? 'ring-2 ring-purple-200' : ''
    }`}>
      {/* Image Container */}
      <div className="relative">
        {compression ? (
          <ImageComparisonView
            originalUrl={originalUrl}
            fileName={file.name}
            compression={compression}
            isLoadingOriginal={isLoadingOriginal}
            isPremium={isPremium}
          />
        ) : (
          <ImageSingleView
            originalUrl={originalUrl}
            fileName={file.name}
            isLoadingOriginal={isLoadingOriginal}
            status={status}
            isPremium={isPremium}
          />
        )}
        
        {/* Remove Button */}
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 z-10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* File Info and Actions */}
      <div className="p-4">
        <FileInfoSection
          fileName={file.name}
          fileSize={file.size}
          compression={compression}
          isPremium={isPremium}
        />
        
        {/* Status (only for non-completed) and Actions */}
        <div className="space-y-3">
          {status !== 'completed' && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Estado:</span>
              <StatusBadge
                status={status}
                retryCount={retryCount}
                maxRetryAttempts={maxRetryAttempts}
                isPremium={isPremium}
              />
            </div>
          )}

          {/* Error Message */}
          {status === 'failed' && error && (
            <div className="text-xs text-destructive bg-destructive/10 p-2 rounded border-l-4 border-destructive/30">
              <strong>Error:</strong> {error}
            </div>
          )}
          
          <ActionButtons
            status={status}
            retryCount={retryCount}
            maxRetryAttempts={maxRetryAttempts}
            compression={compression}
            fileName={file.name}
            onCompress={onCompress}
            onRetry={onRetry}
            isPremium={isPremium}
          />
        </div>
      </div>
    </div>
  );
};

export default ImagePreview;
